import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList,
  Loader2, RefreshCw, ShieldAlert, Truck, CreditCard, AlertTriangle,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Dry-run Bridge' },
  { id: 3, label: 'Review' },
  { id: 4, label: 'Apply' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtGBP(v) {
  if (v == null) return '—';
  const n = parseFloat(v);
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toFixed(2)}`;
}

function fmtNum(v) {
  if (v == null) return '—';
  return parseFloat(v).toFixed(4);
}

function DocTypeBadge({ type }) {
  const styles = {
    delivery: 'bg-blue-50 text-blue-700 border-blue-200',
    credit: 'bg-violet-50 text-violet-700 border-violet-200',
    adjustment: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const icons = { delivery: <Truck size={11} className="mr-0.5" />, credit: <CreditCard size={11} className="mr-0.5" />, adjustment: <AlertTriangle size={11} className="mr-0.5" /> };
  return (
    <Badge variant="outline" className={`text-xs font-medium inline-flex items-center ${styles[type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {icons[type]} {type}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const cls = {
    planned: 'bg-amber-50 text-amber-700 border-amber-200',
    applied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    importable: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    held: 'bg-red-50 text-red-700 border-red-200',
    draft: 'bg-slate-100 text-slate-600 border-slate-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return <Badge variant="outline" className={`text-xs font-medium ${cls}`}>{status?.replace(/_/g, ' ')}</Badge>;
}

// ── Step navigator ─────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav-s3">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-s3-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// ── Source summary banner ──────────────────────────────────────────────────────

function SourceSummary({ session }) {
  if (!session) return null;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 mb-4"
      data-testid="source-summary-s3"
    >
      <div className="grid grid-cols-3 gap-x-6 gap-y-1">
        <span><span className="font-semibold">Session:</span> {session.session_reference}</span>
        <span><span className="font-semibold">Date:</span> {session.session_date}</span>
        <span><span className="font-semibold">Route:</span> B (Historical Draft)</span>
        <span><span className="font-semibold">Period:</span> {session.period_from} → {session.period_to}</span>
        <span><span className="font-semibold">Site:</span> {session.site_id}</span>
        <span><span className="font-semibold">Status:</span> <span className="capitalize">{session.status}</span></span>
      </div>
    </div>
  );
}

// ── Bridge panel ──────────────────────────────────────────────────────────────

function BridgePanel({ bridge }) {
  if (!bridge) return null;
  const b = typeof bridge === 'string' ? JSON.parse(bridge) : bridge;
  const reconciles = b.reconciles === true || b.reconciles === 'true';

  function MetricRow({ label, src_docs, src_lines, src_total, imp_docs, imp_lines, imp_total, held_docs, held_lines, held_total, always_held }) {
    return (
      <div className="border-b border-slate-100 last:border-b-0 pb-3 mb-3 last:mb-0">
        <div className="text-xs font-semibold text-slate-700 mb-2">{label}</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { label: 'Source', docs: src_docs, lines: src_lines, total: src_total, color: 'text-slate-800' },
            { label: always_held ? '— (always held)' : 'Importable', docs: always_held ? 0 : imp_docs, lines: always_held ? 0 : imp_lines, total: always_held ? '0.0000' : imp_total, color: 'text-emerald-700' },
            { label: 'Held', docs: held_docs, lines: held_lines, total: held_total, color: 'text-red-700' },
          ].map(col => (
            <div key={col.label} className="bg-slate-50 rounded-md p-2">
              <div className="text-xs text-slate-500 mb-1">{col.label}</div>
              <div className={`text-base font-bold ${col.color}`}>{fmtGBP(col.total)}</div>
              <div className="text-xs text-slate-400">{col.docs ?? 0} doc{(col.docs ?? 0) !== 1 ? 's' : ''} / {col.lines ?? 0} lines</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="s3-bridge-panel"
      className={`rounded-lg border p-4 ${reconciles ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-slate-700">Valuation Bridge (5-metric)</span>
        {reconciles
          ? <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">Reconciles</Badge>
          : <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-xs">Mismatch</Badge>
        }
      </div>

      <MetricRow
        label="Deliveries"
        src_docs={b.delivery_source_docs} src_lines={b.delivery_source_lines} src_total={b.delivery_source_total}
        imp_docs={b.delivery_importable_docs} imp_lines={b.delivery_importable_lines} imp_total={b.delivery_importable_total}
        held_docs={b.delivery_held_docs} held_lines={b.delivery_held_lines} held_total={b.delivery_held_total}
      />
      <MetricRow
        label="Supplier Credits"
        src_docs={b.credit_source_docs} src_lines={b.credit_source_lines} src_total={b.credit_source_total}
        imp_docs={b.credit_importable_docs} imp_lines={b.credit_importable_lines} imp_total={b.credit_importable_total}
        held_docs={b.credit_held_docs} held_lines={b.credit_held_lines} held_total={b.credit_held_total}
      />
      <MetricRow
        label="Adjustments (auto-held)"
        src_docs={b.adjustment_docs} src_lines={b.adjustment_lines} src_total={b.adjustment_total}
        imp_docs={0} imp_lines={0} imp_total="0.0000"
        held_docs={b.adjustment_docs} held_lines={b.adjustment_lines} held_total={b.adjustment_total}
        always_held
      />

      {/* Net bridge */}
      <div className="mt-3 pt-3 border-t border-slate-200">
        <div className="text-xs font-semibold text-slate-700 mb-2">Net</div>
        <div className="grid grid-cols-3 gap-2 text-center text-sm">
          <div><div className="text-xs text-slate-500">Source</div><div className="font-bold text-slate-800" data-testid="s3-net-source">{fmtGBP(b.net_source_total)}</div></div>
          <div><div className="text-xs text-slate-500">Importable</div><div className="font-bold text-emerald-700" data-testid="s3-net-importable">{fmtGBP(b.net_importable_total)}</div></div>
          <div><div className="text-xs text-slate-500">Held</div><div className="font-bold text-red-700" data-testid="s3-net-held">{fmtGBP(b.net_held_total)}</div></div>
        </div>
        <div className="mt-2 text-xs text-center text-slate-500">
          Importable {fmtGBP(b.net_importable_total)} + Held {fmtGBP(b.net_held_total)}
          {' '}= {fmtGBP((parseFloat(b.net_importable_total || 0) + parseFloat(b.net_held_total || 0)).toFixed(4))}
          {reconciles ? ' ✓ matches source' : ' ← mismatch'}
        </div>
      </div>
    </div>
  );
}

// ── Document rows table ────────────────────────────────────────────────────────

const DOC_TABS = [
  { key: 'deliveries', label: 'Deliveries', icon: <Truck size={13} /> },
  { key: 'credits', label: 'Credits', icon: <CreditCard size={13} /> },
  { key: 'adjustments', label: 'Adjustments', icon: <AlertTriangle size={13} /> },
  { key: 'all', label: 'All', icon: null },
];

function DocumentRowsTable({ planId, category, setCategory }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 25;

  const load = useCallback(async (cat, pg) => {
    if (!planId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/psb-stage3/plans/${planId}?category=${cat}&page=${pg}&page_size=${PAGE_SIZE}`
      );
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load document rows');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    setPage(1);
    load(category, 1);
  }, [category, planId, load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="s3-document-rows-table">
      <div className="flex gap-2 mb-3">
        {DOC_TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`s3-rows-tab-${tab.key}`}
            onClick={() => setCategory(tab.key)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${category === tab.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
        {loading && <Loader2 size={14} className="animate-spin text-slate-400 self-center ml-1" />}
        <span className="self-center ml-auto text-xs text-slate-400">{total} rows</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-slate-500 uppercase tracking-wide">
              <th className="py-2 px-3 font-semibold">Type</th>
              <th className="py-2 px-3 font-semibold">Supplier</th>
              <th className="py-2 px-3 font-semibold">Del.Note</th>
              <th className="py-2 px-3 font-semibold">Date</th>
              <th className="py-2 px-3 font-semibold">Prod Code</th>
              <th className="py-2 px-3 font-semibold">Matched Product</th>
              <th className="py-2 px-3 font-semibold text-right">Qty</th>
              <th className="py-2 px-3 font-semibold text-right">Unit Cost</th>
              <th className="py-2 px-3 font-semibold text-right">Line Value</th>
              <th className="py-2 px-3 font-semibold">Status</th>
              <th className="py-2 px-3 font-semibold">Hold Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr><td colSpan="11" className="py-8 text-center text-slate-400">No rows</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`s3-row-${r.id}`}>
                <td className="py-1.5 px-3"><DocTypeBadge type={r.doc_type} /></td>
                <td className="py-1.5 px-3 font-mono text-slate-700">{r.source_supplier}</td>
                <td className="py-1.5 px-3 font-mono text-slate-600">{r.source_delnote}</td>
                <td className="py-1.5 px-3 text-slate-500">{r.source_date}</td>
                <td className="py-1.5 px-3 font-mono text-slate-700">{r.source_product_code}</td>
                <td className="py-1.5 px-3 text-slate-600 max-w-[180px] truncate">{r.bp_name_snapshot || '—'}</td>
                <td className="py-1.5 px-3 text-right text-slate-700">{fmtNum(r.source_quantity)}</td>
                <td className="py-1.5 px-3 text-right text-slate-600">{r.source_unit_cost != null ? fmtGBP(r.source_unit_cost) : '—'}</td>
                <td className={`py-1.5 px-3 text-right font-medium ${parseFloat(r.source_line_value || 0) < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                  {r.source_line_value != null ? fmtGBP(r.source_line_value) : '—'}
                </td>
                <td className="py-1.5 px-3"><StatusBadge status={r.resolution_status} /></td>
                <td className="py-1.5 px-3 text-slate-400 text-xs max-w-[160px] truncate">{r.hold_reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>{total} total rows</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => { const p = page - 1; setPage(p); load(category, p); }}>Prev</Button>
            <span className="self-center">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(category, p); }}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PsbStage3Page() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const restoredRef = useRef(false);

  // ── Wizard state ─────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // Step 1 — source session
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Plan
  const [planId, setPlanId] = useState('');
  const [planData, setPlanData] = useState(null);
  const [rowCategory, setRowCategory] = useState('deliveries');
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Apply
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Env banner
  const [envLabel, setEnvLabel] = useState('');
  const [envColor, setEnvColor] = useState('');

  // ── Boot: env → sessions → restore ───────────────────────────────────────
  useEffect(() => {
    api.get('/admin/product-master/environment')
      .then(r => { setEnvLabel(r.data.label || ''); setEnvColor(r.data.color || 'amber'); })
      .catch(() => {});
    bootLoad();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bootLoad = async () => {
    setLoadingSessions(true);
    let sessList = [];
    try {
      const res = await api.get('/admin/psb-stage3/s2-sessions');
      sessList = res.data.sessions || [];
      setSessions(sessList);
      // Auto-select session 120 (controlled fixture)
      const fixture = sessList.find(s => s.session_id === 120) || sessList[0];
      if (fixture) { setSelectedSessionId(fixture.session_id); setSelectedSession(fixture); }
    } catch {
      toast.error('Failed to load Stage 2 sessions');
    } finally {
      setLoadingSessions(false);
    }

    if (restoredRef.current) return;
    const pid = searchParams.get('planId');
    if (pid) {
      restoredRef.current = true;
      await restorePlan(pid, sessList);
    }
  };

  const restorePlan = async (pid, sessHint) => {
    try {
      const res = await api.get(`/admin/psb-stage3/plans/${pid}?category=deliveries&page=1`);
      const p = res.data.plan;
      if (!p) { toast.error('Plan not found — URL may be stale'); return; }

      setPlanId(pid);
      setPlanData(p);
      setSelectedSessionId(p.s2_session_id);

      const pool = sessHint || sessions;
      const sess = pool.find(s => s.session_id === p.s2_session_id);
      if (sess) setSelectedSession(sess);

      if (p.status === 'applied') {
        setStep(4);
        setApplyResult({
          idempotent: true,
          plan_id: pid,
          deliveries_created: 0,
          credits_created: 0,
          lines_created: 0,
          bridge: p.bridge,
          applied_at: p.applied_at,
        });
      } else {
        setStep(3);
      }
    } catch (e) {
      toast.error(`Could not restore plan: ${e?.response?.data?.detail || e?.message || 'unknown'}`);
    }
  };

  const setPlanIdAndUrl = (pid) => {
    setPlanId(pid);
    setSearchParams({ planId: pid }, { replace: true });
  };

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get('/admin/psb-stage3/s2-sessions');
      setSessions(res.data.sessions || []);
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  // ── Step navigation ───────────────────────────────────────────────────────

  const goStep2 = async () => {
    if (!selectedSessionId) { toast.error('Select a session first'); return; }
    setCreatingPlan(true);
    try {
      const res = await api.post('/admin/psb-stage3/plan', {
        s2_session_id: selectedSessionId,
        notes: `PSB Stage 3 plan — session ${selectedSessionId}`,
      });
      const plan = res.data.plan;
      setPlanData(plan);
      setPlanIdAndUrl(plan.run_id);
      setStep(2);
      toast.success(res.data.idempotent ? 'Restored existing dry-run plan' : 'Dry-run plan created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleApply = async () => {
    if (!planId) return;
    setApplying(true);
    try {
      const res = await api.post(`/admin/psb-stage3/plans/${planId}/apply`);
      setApplyResult(res.data);
      const planRes = await api.get(`/admin/psb-stage3/plans/${planId}?category=deliveries&page=1`);
      setPlanData(planRes.data.plan);
      setStep(4);
      toast.success(res.data.idempotent
        ? 'Idempotent apply — existing documents returned'
        : `Applied: ${res.data.deliveries_created} deliveries, ${res.data.credits_created} credits, ${res.data.lines_created} lines`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="psb-stage3-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate('/stocktaking/psb-stage2-import')}
          data-testid="back-to-stage2"
          className="text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft size={16} className="mr-1" /> Stage 2
        </Button>
        <span className="text-slate-300">/</span>
        <h1 className="text-xl font-semibold text-slate-900">PSB Stage 3 — Deliveries &amp; Supplier Credits</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4 ml-[72px]">
        Leicester V32 · Route B · Session 120 · Preview only · Draft documents only
      </p>

      {envLabel && (
        <div
          data-testid="env-banner-s3"
          className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${envColor === 'red' ? 'border-red-300 bg-red-50 text-red-700' : 'border-amber-300 bg-amber-50 text-amber-700'}`}
        >
          <ShieldAlert size={15} />
          {envLabel} — Stage 3 writes are gated to Preview / fixture site only.
        </div>
      )}

      <StepNav current={step} />

      {/* ── Step 1: Source session ──────────────────────────────────────── */}
      {step === 1 && (
        <Card data-testid="s3-step-1-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList size={16} /> Select Stage 2 Source Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={loadSessions} disabled={loadingSessions} data-testid="s3-refresh-sessions-btn">
                <RefreshCw size={14} className={loadingSessions ? 'animate-spin' : ''} />
              </Button>
              <span className="text-xs text-slate-500">{sessions.length} Route B session(s) available</span>
            </div>

            {sessions.length === 0 && !loadingSessions && (
              <p className="text-sm text-slate-400 py-4 text-center">
                No applied Route B Stage 2 sessions found for the fixture site.
              </p>
            )}

            <div className="space-y-2">
              {sessions.map(s => (
                <div
                  key={s.session_id}
                  data-testid={`s3-session-row-${s.session_id}`}
                  onClick={() => { setSelectedSessionId(s.session_id); setSelectedSession(s); }}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${selectedSessionId === s.session_id ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-sm text-slate-800">{s.session_reference}</span>
                      <span className="text-xs text-slate-400 ml-2">Session {s.session_id}</span>
                    </div>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Site {s.site_id} · {s.session_date} · Route {s.route}
                    {s.period_from && ` · ${s.period_from} → ${s.period_to || 'open'}`}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={goStep2}
                disabled={!selectedSessionId || creatingPlan}
                data-testid="s3-create-plan-btn"
              >
                {creatingPlan ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Build Dry-run Plan <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Bridge ──────────────────────────────────────────────── */}
      {step === 2 && planData && (
        <Card data-testid="s3-step-2-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Dry-run Bridge</span>
              <StatusBadge status={planData.status} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SourceSummary session={selectedSession} />
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg border p-3 flex gap-6">
              <span><span className="font-medium">Plan ID:</span> <span className="font-mono" data-testid="s3-plan-id">{planId}</span></span>
              <span><span className="font-medium">Created:</span> {planData.created_at?.slice(0, 16)}</span>
            </div>
            <BridgePanel bridge={planData.bridge} />
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="s3-back-to-step1-btn">
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button onClick={() => setStep(3)} data-testid="s3-review-btn">
                Review Rows <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 3: Review rows ─────────────────────────────────────────── */}
      {step === 3 && planData && (
        <Card data-testid="s3-step-3-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Document Row Review</span>
              <StatusBadge status={planData.status} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SourceSummary session={selectedSession} />
            <BridgePanel bridge={planData.bridge} />
            <DocumentRowsTable
              planId={planId}
              category={rowCategory}
              setCategory={setRowCategory}
            />
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="s3-back-to-step2-btn">
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button
                onClick={handleApply}
                disabled={applying || planData.status === 'applied'}
                data-testid="s3-apply-btn"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                {applying ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {planData.status === 'applied' ? 'Already Applied' : 'Apply — Create Draft Documents'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 4: Applied ─────────────────────────────────────────────── */}
      {step === 4 && (
        <Card data-testid="s3-step-4-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" />
              {applyResult?.idempotent ? 'Idempotent — Existing Documents' : 'Draft Documents Created'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {applyResult && (
              <>
                <SourceSummary session={selectedSession} />
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2">
                  <div className="text-sm font-semibold text-emerald-800 mb-2">Apply Summary</div>
                  <div className="text-xs text-slate-600 grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-0.5">Deliveries</div>
                      <div className="text-lg font-bold text-blue-700" data-testid="s3-deliveries-count">
                        {applyResult.idempotent ? '—' : applyResult.deliveries_created}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-0.5">Credits</div>
                      <div className="text-lg font-bold text-violet-700" data-testid="s3-credits-count">
                        {applyResult.idempotent ? '—' : applyResult.credits_created}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 mb-0.5">Lines</div>
                      <div className="text-lg font-bold text-slate-700" data-testid="s3-lines-count">
                        {applyResult.idempotent ? '—' : applyResult.lines_created}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 pt-1">Applied at: {applyResult.applied_at?.slice(0, 16)}</div>
                </div>

                {planData?.bridge && <BridgePanel bridge={planData.bridge} />}

                {applyResult.idempotent && (
                  <div
                    className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
                    data-testid="s3-idempotent-notice"
                  >
                    Idempotency gate: this plan was already applied. Existing draft documents are returned unchanged — no duplicates created.
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(3)} data-testid="s3-back-to-step3-btn">
                    <ArrowLeft size={14} className="mr-1" /> Back to Review
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleApply}
                    disabled={applying}
                    data-testid="s3-repeat-apply-btn"
                    className="text-slate-600"
                  >
                    {applying ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                    Re-apply (Idempotency Test)
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
